import { useState, useCallback, useEffect, useRef } from "react";
import axios from "../services/axios";

export interface LiveDataHistoryPoint {
  timestamp: number;
  power_soliseco?: number;
  voltage_soliseco?: number;
  water_temperature?: number;
  circulator_state?: string;
  valve_state?: string;
  heating_percentage_power?: number;
}

interface UseLiveDataHistoryOptions {
  deviceId: string | undefined;
  enabled: boolean;
  hours?: number;
  frequency?: number;
}

export const useLiveDataHistory = ({
  deviceId,
  enabled,
  hours = 2,
  frequency = 15000,
}: UseLiveDataHistoryOptions) => {
  const [data, setData] = useState<LiveDataHistoryPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const fetchHistory = useCallback(async () => {
    if (!deviceId || !enabled) return;

    try {
      setLoading(true);
      const response = await axios.get(
        `/api-v1/device/${deviceId}/live-data/history/`,
        { params: { hours } }
      );
      setData(response.data || []);
      setError(null);
    } catch (err: any) {
      setError(
        err.response?.data?.error || err.message || "Failed to fetch live data history"
      );
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [deviceId, enabled, hours]);

  useEffect(() => {
    setData([]);
  }, [deviceId]);

  useEffect(() => {
    if (pollingIntervalRef.current) {
      clearInterval(pollingIntervalRef.current);
      pollingIntervalRef.current = null;
    }

    if (enabled && deviceId) {
      fetchHistory();
      const interval = setInterval(fetchHistory, frequency);
      pollingIntervalRef.current = interval;
      return () => clearInterval(interval);
    }
  }, [enabled, deviceId, frequency, fetchHistory]);

  return {
    data,
    loading,
    error,
    refetch: fetchHistory,
    clearError: () => setError(null),
  };
};
